import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { clonePromoStoreProduct } from "../../../../../components/SelectedPresentation/promoStoreCopy";

const getStoreLabel = (store = {}) =>
  String(store.ShopName || store.title || store.path || `PromoStore #${store.id}`).trim();

const normalizeProducts = (order = {}) =>
  (Array.isArray(order?.Products) ? order.Products : [])
    .filter(Boolean)
    .map((product) => clonePromoStoreProduct(product));

const CopyProductsToPromoStoreDialog = ({
  open,
  onClose,
  order,
  products: sourceProducts,
  instanceUniqueId,
  sourceLabel = "document",
  onComplete,
}) => {
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [loadingStores, setLoadingStores] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const products = useMemo(
    () => (Array.isArray(sourceProducts) ? sourceProducts.filter(Boolean).map(clonePromoStoreProduct) : normalizeProducts(order)),
    [order, sourceProducts],
  );

  useEffect(() => {
    if (!open) return;
    let mounted = true;

    const loadStores = async () => {
      setLoadingStores(true);
      setError("");
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/eligible-copy-promostores`,
          { params: { instanceUniqueId } },
        );
        if (!mounted) return;

        const available = Array.isArray(response?.data?.promostores)
          ? response.data.promostores.filter((store) => {
              const allowed = store?.InstanceUniqueIds;
              if (!instanceUniqueId || !Array.isArray(allowed) || allowed.length === 0) return true;
              return allowed.map(String).includes(String(instanceUniqueId));
            })
          : [];

        setStores(available);
        if (available.length === 1) {
          setSelectedStoreId(String(available[0].id));
        }
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.message || "Unable to load PromoStores.");
      } finally {
        if (mounted) setLoadingStores(false);
      }
    };

    loadStores();
    return () => {
      mounted = false;
    };
  }, [open, instanceUniqueId]);

  useEffect(() => {
    if (!open) {
      setSelectedStoreId("");
      setError("");
      setSaving(false);
    }
  }, [open]);

  const handleCopy = async () => {
    if (!selectedStoreId || !products.length || saving || !stores.some((store) => String(store.id) === String(selectedStoreId))) return;

    setSaving(true);
    setError("");

    try {
      const detailResponse = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/get-promostore/${selectedStoreId}`,
      );
      const promoStore = detailResponse?.data?.promostore;
      if (!promoStore) {
        throw new Error("PromoStore details were not returned.");
      }

      const existingItems = Array.isArray(promoStore.Items)
        ? promoStore.Items
        : Array.isArray(promoStore.Products)
          ? promoStore.Products
          : [];

      await axios.put(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/edit-promostore/${selectedStoreId}`,
        {
          updateScope: "products",
          items: [...existingItems, ...products],
        },
      );

      onComplete?.({
        promoStoreId: Number(selectedStoreId),
        productCount: products.length,
        sourceLabel,
      });
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Unable to copy these products to the PromoStore.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(open)} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Convert to PromoStore</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Copy {products.length} product{products.length === 1 ? "" : "s"} from this {sourceLabel} into an existing PromoStore.
          Existing store products will be kept.
        </Typography>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {!products.length ? (
          <Alert severity="warning">There are no products available to copy.</Alert>
        ) : null}

        {loadingStores ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading PromoStores...</Typography>
          </Box>
        ) : (
          <TextField
            select
            fullWidth
            label="PromoStore"
            value={selectedStoreId}
            onChange={(event) => setSelectedStoreId(event.target.value)}
            disabled={!stores.length || saving}
            helperText={stores.length ? "Choose an assigned PromoStore to receive these products." : "No eligible PromoStores. Ask an administrator to assign you to a store."}
          >
            {stores.map((store) => (
              <MenuItem key={store.id} value={String(store.id)}>
                {getStoreLabel(store)}
              </MenuItem>
            ))}
          </TextField>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCopy}
          disabled={saving || loadingStores || !selectedStoreId || !products.length}
        >
          {saving ? "Copying..." : "Copy products"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyProductsToPromoStoreDialog;
